/**
 * Brand Product Detail Page - Convex Migration
 * Industry-aware: Uses unified ProductDisplay component for all industries
 * 
 * Uses Convex real-time queries with client-side pattern
 */

"use client";

import { use, useEffect, useRef } from "react";
import { useQuery } from "convex/react";
import { api } from "@/convex/_generated/api";
import { useRouter } from "next/navigation";
import { trackProductView } from "@/lib/analytics/gtag";
import ShopHeader from "../../components/ShopHeader";
import { ProductDisplay } from "@/components/product/ProductDisplay";
import { monthDay } from "@/lib/config/collections";

interface ProductPageProps {
  params: Promise<{ domain: string; slug: string }>;
}

/**
 * Loading skeleton — mirrors the real product page layout exactly
 */
function ProductSkeleton() {
  return (
    <div className="min-h-screen" style={{ backgroundColor: "#FBFCFD" }}>
      {/* Back nav */}
      <div className="bg-white border-b border-gray-100">
        <div className="max-w-[1400px] mx-auto px-4 md:px-6 lg:px-8 py-3">
          <div className="h-3 w-24 bg-gray-200 rounded animate-pulse" />
        </div>
      </div>

      {/* Main grid */}
      <div className="pt-4 lg:pt-6 pb-20 px-4 md:px-6 lg:px-8 max-w-[1400px] mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-10">

          {/* Left — Gallery */}
          <div className="lg:col-span-7 flex flex-col-reverse md:flex-row gap-3 lg:gap-4">
            {/* Thumbnails */}
            <div className="flex md:flex-col gap-2">
              {[1, 2, 3].map((i) => (
                <div key={i} className="shrink-0 w-14 h-20 md:w-16 md:h-24 bg-gray-200 rounded animate-pulse" />
              ))}
            </div>
            {/* Main image */}
            <div className="flex-1 bg-gray-200 rounded-lg animate-pulse" style={{ minHeight: 480 }} />
          </div>

          {/* Right — Details */}
          <div className="lg:col-span-5 space-y-5">
            {/* Brand + title */}
            <div className="space-y-2 border-b border-gray-100 pb-4">
              <div className="h-3 w-28 bg-gray-200 rounded animate-pulse" />
              <div className="h-7 w-3/4 bg-gray-200 rounded animate-pulse" />
              <div className="h-5 w-1/3 bg-gray-200 rounded animate-pulse" />
            </div>

            {/* Colour swatches */}
            <div className="space-y-2">
              <div className="h-3 w-12 bg-gray-200 rounded animate-pulse" />
              <div className="flex gap-2.5">
                {[1, 2, 3, 4].map((i) => (
                  <div key={i} className="w-10 h-10 rounded-full bg-gray-200 animate-pulse" />
                ))}
              </div>
            </div>

            {/* Size grid */}
            <div className="space-y-2">
              <div className="h-3 w-8 bg-gray-200 rounded animate-pulse" />
              <div className="grid grid-cols-5 gap-1.5">
                {[1, 2, 3, 4, 5].map((i) => (
                  <div key={i} className="h-8 bg-gray-200 rounded animate-pulse" />
                ))}
              </div>
            </div>

            {/* Add to cart button */}
            <div className="flex gap-3 pt-2">
              <div className="flex-1 h-12 bg-gray-200 rounded animate-pulse" />
              <div className="w-12 h-12 bg-gray-200 rounded animate-pulse" />
            </div>

            {/* Description */}
            <div className="space-y-2 pt-3 border-t border-gray-100">
              <div className="h-3 w-32 bg-gray-200 rounded animate-pulse" />
              <div className="space-y-1.5">
                <div className="h-3 w-full bg-gray-200 rounded animate-pulse" />
                <div className="h-3 w-5/6 bg-gray-200 rounded animate-pulse" />
                <div className="h-3 w-4/6 bg-gray-200 rounded animate-pulse" />
              </div>
            </div>
          </div>
        </div>

        {/* Related products */}
        <div className="mt-16 space-y-4">
          <div className="h-5 w-40 bg-gray-200 rounded animate-pulse" />
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="space-y-2">
                <div className="aspect-[3/4] bg-gray-200 rounded-lg animate-pulse" />
                <div className="h-3 w-3/4 bg-gray-200 rounded animate-pulse" />
                <div className="h-3 w-1/2 bg-gray-200 rounded animate-pulse" />
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * Unified product content component — works for both fashion and electronics
 */
function UnifiedProductContent({
  brand,
  product,
  relatedProducts,
  collectionSections,
  shareUrl,
  industry,
}: {
  brand: any;
  product: any;
  relatedProducts: any[];
  collectionSections: any[];
  shareUrl: string;
  industry: "fashion" | "electronics";
}) {
  return (
    <ProductDisplay
      product={{
        _id: product._id,
        name: product.name,
        slug: product.slug || product.name.toLowerCase().replace(/\s+/g, "-"),
        price: product.price,
        compareAtPrice: product.compareAtPrice,
        images: product.images || [product.imageUrl],
        variants: product.fashionAttributes?.variants || product.variants,
        stock: product.stock,
        totalStock: product.totalStock,
        description: product.description,
        isActive: product.isActive,
        composition: product.composition,
        careInstructions: product.careInstructions,
        shippingInfo: product.shippingInfo,
        fashionAttributes: product.fashionAttributes,
        electronicsAttributes: product.electronicsAttributes,
        // New or used — its badge under the name (lib/config/productCondition.ts).
        condition: product.condition,
        // The shop's own details (customSpecs) for the Specifications card.
        dynamicAttributes: product.dynamicAttributes,
        hidePrice: product.hidePrice,
        hasCustomPrice: product.hasCustomPrice,
        // Food & Beverage
        food: product.food,
        trackStock: product.trackStock,
        industry: product.industry,
        // Its catalogue shelf — the Size Guide stays off Event Decor / flowers (components/product/sizeGuide.ts).
        category: product.category,
        subcategory: product.subcategory,
        productType: product.productType,
        // Pre-order: sellable at 0 stock — the display reads product.preorder.
        preorder: product.preorder,
        // For hire (2026-09): the hire panel needs the rates, the kit and the
        // brand (for the house rules and the request). This mapping is an
        // allow-list — without these three lines the panel never renders.
        brandId: product.brandId,
        hire: product.hire,
        // Also the add-ons on a sale ("Comes with", the extras to tick) — Oct 2026.
        hireKit: product.hireKit,
        // "Free delivery" on website orders (lib/config/websiteAddOns.ts).
        freeDeliveryOnline: product.freeDeliveryOnline,
        // Size & measurements, and whether the shop shows sizes (lib/config/dimensions.ts).
        dimensions: product.dimensions,
        showSizesOnWebsite: product.showSizesOnWebsite,
      } as any}
      brand={{
        _id: brand._id,
        name: brand.name,
        slug: brand.slug,
        subdomain: brand.subdomain,
        totalProducts: brand.totalProducts,
        followersCount: brand.followersCount,
      }}
      industry={industry}
      relatedProducts={relatedProducts}
      collectionSections={collectionSections}
      shareUrl={shareUrl}
      productUrlPrefix=""
      backLink="/"
      backLinkText={brand.name.toUpperCase()}
      brandPageUrl="/"
    />
  );
}

/**
 * Product content component - uses Convex queries
 */
function ProductContent({
  domain,
  slug,
}: {
  domain: string;
  slug: string;
}) {
  const router = useRouter();

  // Get brand by subdomain
  const brand = useQuery(api.brands.core.getBrandBySubdomain, {
    subdomain: domain,
  });

  // Get product by slug (only if brand is loaded)
  const product = useQuery(
    api.products.shop.getProductBySlug,
    brand?._id
      ? {
        brandId: brand._id,
        slug: slug,
      }
      : "skip"
  );

  // Get related products (only if product is loaded)
  const relatedProducts = useQuery(
    api.products.shop.getRelatedProducts,
    product?.categoryId && brand?._id
      ? {
        brandId: brand._id,
        categoryId: product.categoryId,
        excludeProductId: product._id,
        limit: 12,
      }
      : "skip"
  );

  // "More in <collection>": the shop's collections this product is in, in
  // season on the visitor's own day. Never holds the page up.
  const collectionSections = useQuery(
    api.collections.shop.productCollectionSections,
    product?._id && brand?._id
      ? { brandId: brand._id, productId: product._id, today: monthDay(new Date()) }
      : "skip"
  );

  // Analytics: report the product view. The trackers in lib/analytics live in
  // ProductDetailsClient, which is the MARKETPLACE component — this storefront
  // route renders its own page, so without this the brand's Google tag saw
  // nothing but page loads (no view_item, no remarketing signal).
  const viewedRef = useRef<string | null>(null);
  useEffect(() => {
    if (!product?._id || viewedRef.current === String(product._id)) return;
    viewedRef.current = String(product._id);
    trackProductView({
      id: String(product._id),
      name: product.name,
      category: (product as any).categoryName,
      price: Number(product.price) || 0,
      brand: brand?.name,
    });
  }, [product?._id, product?.name, product?.price, brand?.name]);

  // Loading state
  if (brand === undefined || product === undefined || relatedProducts === undefined) {
    return <ProductSkeleton />;
  }

  // Brand not found
  if (!brand) {
    router.push("/404");
    return null;
  }

  // Product not found
  if (!product) {
    router.push("/404");
    return null;
  }

  // Determine industry - default to fashion if not specified
  const industry = product.industry || brand.industry || "fashion";

  // Combine mockup into product images array (safeguarding for UI renderers)
  const displayProduct = {
    ...product,
    images: [
      ...(product.images || [product.imageUrl] || []),
      ...(product.structuredImages?.mockup?.url ? [product.structuredImages.mockup.url] : [])
    ]
  };

  // Build share URL — prefer the EXACT host the visitor is on (their custom
  // domain if they arrived via it), falling back to the brand's active custom
  // domain, else the assigned subdomain. Never the old, wrong
  // "<sub>.<industry>.kuttostechnologies.com" host (that 404s).
  const industryDomain =
    brand.industry === "electronics"
      ? "kuttostechnologies.com"
      : brand.industry === "retail"
        ? "kuttosretail.com"
        : "kuttosfashion.com";
  const shareUrl =
    typeof window !== "undefined"
      ? `${window.location.origin}/product/${slug}`
      : brand.customDomain && brand.customDomainConfig?.status === "active"
        ? `https://${brand.customDomain}/product/${slug}`
        : `https://${brand.subdomain}.${industryDomain}/product/${slug}`;

  // Fashion products use the ProductDisplay component. A registry industry
  // mapped onto the fashion TEMPLATE carries a fashionAttributes bucket —
  // bucket presence decides the layout, so template-mapped products render
  // with the right selectors too.
  if (industry === "fashion" || !!(product as any).fashionAttributes) {
    return (
      <UnifiedProductContent
        brand={brand}
        product={displayProduct}
        relatedProducts={relatedProducts || []}
        collectionSections={collectionSections || []}
        shareUrl={shareUrl}
        industry="fashion"
      />
    );
  }

  // Electronics and all other industries also use unified ProductDisplay
  return (
    <UnifiedProductContent
      brand={brand}
      product={displayProduct}
      relatedProducts={relatedProducts || []}
      collectionSections={collectionSections || []}
      shareUrl={shareUrl}
      industry="electronics"
    />
  );
}

/**
 * Page component - client-side with Convex queries
 */
export default function ProductPageClient({ params }: ProductPageProps) {
  const { domain, slug } = use(params);

  return (
    <div className="min-h-screen" style={{ backgroundColor: "#FBFCFD" }}>
      <ShopHeader brandName="" />
      <ProductContent domain={domain} slug={slug} />
    </div>
  );
}
